/**
 * Toast Event System
 *
 * Enables showing toast notifications from non-React code (e.g., Axios interceptors).
 * Uses a simple pub/sub pattern to bridge imperative code with React's declarative UI.
 *
 * Usage from Axios interceptors or other non-React code:
 *   import { emitToast } from '../utils/toastEvents';
 *   emitToast({ type: 'error', title: 'Access Denied', message: 'You do not have permission' });
 */

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastEvent {
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

type ToastListener = (event: ToastEvent) => void;

// Simple event emitter for toast notifications
const listeners: Set<ToastListener> = new Set();

/**
 * Subscribe to toast events (used by ToastContext)
 */
export function subscribeToToasts(listener: ToastListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Emit a toast event (used by Axios interceptors and other non-React code)
 */
export function emitToast(event: ToastEvent): void {
  listeners.forEach((listener) => {
    listener(event);
  });
}

/**
 * Helper to emit a 403 Forbidden toast with API message
 */
export function emit403Toast(apiMessage?: string): void {
  emitToast({
    type: "warning",
    title: "Access Denied",
    message: apiMessage ?? "You do not have permission to perform this action.",
    duration: 5000,
  });
}
